import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import InstallInstructions, { clearInstallDismiss } from "../components/InstallInstructions";
import InstallSection from "../components/InstallSection";
import StripeSetup from "../components/StripeSetup";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { TermsModal } from "../components/Terms";
import { Button, Field } from "../components/ui";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { apiGet, apiSend } from "../api";
import { C, R, S } from "../theme";

const card = { background: C.card, borderRadius: R.md, padding: S.lg, border: `1px solid ${C.border}`, marginBottom: S.md };
const linkCard = { display: "flex", alignItems: "center", gap: S.md, ...card, cursor: "pointer" };
const section = { color: C.text, fontSize: 18, fontWeight: 800, marginTop: S.md, marginBottom: S.sm };

function Modal({ children, onClose }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", background: C.card, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: S.lg, paddingBottom: S.xxl, width: "100%", maxWidth: 480 }}>
        <button data-testid="modal-close-x" onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, width: 34, height: 34, borderRadius: 999, background: C.card2, border: `1px solid ${C.border}`, color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}><Icon name="close" size={20} color={C.text} /></button>
        {children}
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, isManager, logout, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [staff, setStaff] = useState([]);
  const [sub, setSub] = useState(null);
  const [staffModal, setStaffModal] = useState(false);
  const [form, setForm] = useState({ display_name: "", username: "", password: "", role: "operator" });
  const [editStaff, setEditStaff] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: "", username: "", password: "", role: "operator" });
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [delStaff, setDelStaff] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [unameStatus, setUnameStatus] = useState("");
  const [editUnameStatus, setEditUnameStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg] = useState("");
  const [autoReport, setAutoReport] = useState(!!user?.auto_monthly_report);
  const [reminders, setReminders] = useState(!!user?.daily_check_reminders);
  const [remindMsg, setRemindMsg] = useState("");
  const [windCheck, setWindCheck] = useState(!!user?.show_wind_check);
  const [installOpen, setInstallOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState("");

  const load = async () => {
    if (isManager) {
      const [s, sb] = await Promise.all([apiGet("/staff").catch(() => []), apiGet("/subscription").catch(() => null)]);
      setStaff(s); setSub(sb);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!staffModal) { setUnameStatus(""); return; }
    const u = form.username.trim().toLowerCase();
    if (u.length < 3) { setUnameStatus(""); return; }
    setUnameStatus("checking");
    const t = setTimeout(async () => {
      try { const r = await apiGet(`/auth/check-availability?username=${encodeURIComponent(u)}`); setUnameStatus(r.username_taken ? "taken" : "ok"); }
      catch { setUnameStatus(""); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.username, staffModal]);

  useEffect(() => {
    if (!editStaff) { setEditUnameStatus(""); return; }
    const u = editForm.username.trim().toLowerCase();
    if (u.length < 3) { setEditUnameStatus(""); return; }
    if (u === (editStaff.username || "").toLowerCase()) { setEditUnameStatus("ok"); return; }
    setEditUnameStatus("checking");
    const t = setTimeout(async () => {
      try { const r = await apiGet(`/auth/check-availability?username=${encodeURIComponent(u)}&exclude_id=${editStaff.id}`); setEditUnameStatus(r.username_taken ? "taken" : "ok"); }
      catch { setEditUnameStatus(""); }
    }, 400);
    return () => clearTimeout(t);
  }, [editForm.username, editStaff]);

  const addStaff = async () => {
    setErr("");
    if (!form.display_name || form.username.length < 3 || form.password.length < 4) { setErr(t("settings.errStaffFields")); return; }
    if (unameStatus === "taken") { setErr(t("settings.errUsernameTaken")); return; }
    setBusy(true);
    try { await apiSend("/staff", "POST", form); setStaffModal(false); setForm({ display_name: "", username: "", password: "", role: "operator" }); load(); }
    catch (e) { setErr(e?.message || t("settings.failed")); } finally { setBusy(false); }
  };
  const openEdit = (s) => { setEditErr(""); setEditForm({ display_name: s.display_name || "", username: s.username || "", password: "", role: s.role || "operator" }); setEditStaff(s); };
  const saveStaffEdit = async () => {
    setEditErr("");
    if (!editForm.display_name || editForm.username.trim().length < 3) { setEditErr(t("settings.errEditFields")); return; }
    if (editForm.password && editForm.password.length < 4) { setEditErr(t("settings.errPasswordLen")); return; }
    if (editUnameStatus === "taken") { setEditErr(t("settings.errUsernameTaken")); return; }
    setEditBusy(true);
    try {
      const payload = { display_name: editForm.display_name, username: editForm.username, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await apiSend(`/staff/${editStaff.id}`, "PATCH", payload);
      setEditStaff(null); load();
    } catch (e) { setEditErr(e?.message || t("settings.updateFailed")); } finally { setEditBusy(false); }
  };
  const confirmRemoveStaff = async () => {
    if (!delStaff) return;
    setDelBusy(true);
    try { await apiSend(`/staff/${delStaff.id}`, "DELETE"); setDelStaff(null); load(); }
    finally { setDelBusy(false); }
  };
  const toggleAutoReport = async () => { const v = !autoReport; setAutoReport(v); await apiSend("/settings/monthly-report", "POST", { enabled: v }); await refresh(); };
  const toggleReminders = async () => {
    const next = !reminders;
    setRemindMsg("");
    if (next) {
      if (!("Notification" in window)) { setRemindMsg(t("settings.notifNotSupported")); return; }
      let perm = Notification.permission;
      if (perm !== "granted") perm = await Notification.requestPermission();
      if (perm !== "granted") { setRemindMsg(t("settings.notifAllow")); return; }
    }
    setReminders(next);
    try { await apiSend("/settings/reminders", "POST", { enabled: next }); await refresh(); } catch {}
  };
  const toggleWindCheck = async () => { const v = !windCheck; setWindCheck(v); try { await apiSend("/settings/wind-check", "POST", { enabled: v }); await refresh(); } catch {} };
  const sendReportNow = async () => {
    setReportMsg(""); setReportBusy(true);
    try { const res = await apiSend("/report/email-monthly", "POST"); setReportMsg(`✅ ${t("settings.sentTo")} ${res.sent_to} (${res.records} ${t("settings.records")})`); }
    catch (e) { setReportMsg("❌ " + (e?.message || t("settings.failedToSend"))); } finally { setReportBusy(false); }
  };
  const sendTestEmail = async () => {
    setTestMsg(""); setTestBusy(true);
    try { const r = await apiSend("/admin/test-email", "POST"); setTestMsg(`✅ ${t("settings.testEmailSentTo")} ${r.sent_to}!`); }
    catch (e) { setTestMsg("❌ " + (e?.message || t("settings.emailFailed"))); } finally { setTestBusy(false); }
  };
  const isMaster = (user?.email || "").trim().toLowerCase() === "will_em@hotmail.co.uk";
  const seatLimit = sub?.seat_limit ?? 9999;
  const capReached = !!sub && sub.is_exempt_from_billing !== true && seatLimit < 9999 && (sub.user_count ?? 1) >= seatLimit;

  return (
    <div className="screen fade-up">
      <h1 className="display" style={{ color: C.text, fontSize: 28, fontWeight: 900, marginBottom: S.md }}>{t("settings.title")}</h1>

      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: S.md }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: C.amber, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="person" size={26} color={C.black} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontSize: 18, fontWeight: 800 }}>{user?.display_name || user?.company_name}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{user?.email || user?.username}</div>
            <div style={{ color: C.amber, fontSize: 11, fontWeight: 800, marginTop: 4, letterSpacing: 1 }}>{user?.role?.toUpperCase()}</div>
          </div>
        </div>
      </div>

      {isManager && (
        <>
          <div style={section}>{t("settings.subscription")}</div>
          <div data-testid="subscription-card" className="pressable" onClick={() => nav("/paywall")} style={{ ...card, cursor: "pointer" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>{sub?.is_exempt_from_billing ? t("settings.vipExempt") : (sub?.tier_label || (sub?.tier || "free").toUpperCase())}</div>
                {sub?.status === "trialing" ? <div style={{ color: C.amber, fontWeight: 800, fontSize: 13, marginTop: 2 }}>{t("settings.freeTrial")} · {sub?.days_remaining ?? 0} {t("settings.daysLeft")}</div>
                  : sub?.status === "active" && !sub?.is_exempt_from_billing ? <div style={{ color: C.green, fontWeight: 700, fontSize: 13, marginTop: 2 }}>{t("settings.active")} · {t("settings.renewsIn")} {sub?.days_remaining ?? 0} {t("settings.days")}</div>
                  : sub?.status === "expired" ? <div style={{ color: C.red, fontWeight: 700, fontSize: 13, marginTop: 2 }}>{t("settings.accessEndedTap")}</div> : null}
                <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{sub?.attraction_count ?? 0}/{sub?.is_exempt_from_billing || sub?.attraction_limit >= 9999 ? "∞" : sub?.attraction_limit ?? 1} {t("settings.attractions")} · {sub?.user_count ?? 1}/{sub?.is_exempt_from_billing || (sub?.seat_limit ?? 9999) >= 9999 ? "∞" : sub?.seat_limit} {t("settings.users")}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 2, background: C.amber, padding: "8px 12px", borderRadius: R.pill }}>
                <span style={{ color: C.black, fontWeight: 800 }}>{t("settings.manage")}</span><Icon name="chevron-forward" size={16} color={C.black} />
              </div>
            </div>
          </div>

          {user?.role === "owner" && (
            <>
              <div style={section}>{t("settings.monthlyReport")}</div>
              <div style={card}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{t("settings.autoEmail")}</div>
                    <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{t("settings.compliancePdfTo")} {user?.email}</div>
                  </div>
                  <button data-testid="auto-report-toggle" onClick={toggleAutoReport} style={{ width: 52, height: 30, borderRadius: 15, background: autoReport ? C.green : C.card2, position: "relative", transition: "background 0.2s" }}>
                    <span style={{ position: "absolute", top: 3, left: autoReport ? 25 : 3, width: 24, height: 24, borderRadius: 12, background: C.text, transition: "left 0.2s" }} />
                  </button>
                </div>
                <div style={{ height: S.md }} />
                <Button title={t("settings.emailReportNow")} variant="dark" testID="send-report-now" onPress={sendReportNow} loading={reportBusy} />
                {!!reportMsg && <div style={{ color: reportMsg.startsWith("✅") ? C.green : C.red, marginTop: S.md, fontWeight: 700 }}>{reportMsg}</div>}
              </div>
            </>
          )}

          {isMaster && <StripeSetup />}

          {isMaster && (
            <>
              <div style={{ ...section, marginTop: S.md }}>{t("settings.adminEmailAlerts")}</div>
              <div style={card}>
                <div style={{ color: C.muted, fontSize: 13, marginBottom: S.md, lineHeight: 1.4 }}>{t("settings.adminAlertsPre")} <b style={{ color: C.text }}>will_em@hotmail.co.uk</b>. {t("settings.adminAlertsPost")}</div>
                <Button title={`📩 ${t("settings.sendTestEmail")}`} variant="dark" testID="send-test-email" onPress={sendTestEmail} loading={testBusy} />
                {!!testMsg && <div data-testid="test-email-status" style={{ color: testMsg.startsWith("✅") ? C.green : C.red, marginTop: S.md, fontWeight: 700, fontSize: 13, lineHeight: 1.4, wordBreak: "break-word" }}>{testMsg}</div>}
              </div>
            </>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: S.md }}>
            <div style={section}>{t("settings.team")} ({staff.length})</div>
            <button data-testid="add-staff-btn" className="pressable" onClick={() => { if (capReached) { setUpgradeOpen(true); return; } setErr(""); setUnameStatus(""); setForm({ display_name: "", username: "", password: "", role: "operator" }); setStaffModal(true); }} style={{ color: capReached ? C.muted : C.amber, fontWeight: 800 }}>{t("settings.addStaff")}</button>
          </div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: S.sm, lineHeight: 1.4 }}>{t("settings.teamHelp")}</div>
          {capReached && <div data-testid="seat-cap-note" style={{ display: "flex", alignItems: "flex-start", gap: S.sm, background: "rgba(245,158,11,0.12)", border: `1px solid ${C.amber}`, borderRadius: R.md, padding: S.md, marginBottom: S.sm }}>
            <Icon name="lock-closed" size={16} color={C.amber} />
            <span style={{ color: C.text, fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{t("settings.seatCapReached")} <b>{sub?.tier_label || (sub?.tier || "").toUpperCase()}</b> {t("settings.planLimitOf")} {seatLimit} {seatLimit !== 1 ? t("settings.usersPlural") : t("settings.user")}. <span data-testid="cap-upgrade-link" onClick={() => nav("/paywall")} style={{ color: C.amber, fontWeight: 800, textDecoration: "underline", cursor: "pointer" }}>{t("settings.upgradeToAddMore")}</span>.</span>
          </div>}
          {staff.length === 0 && <div data-testid="no-staff" style={{ color: C.muted, fontSize: 14, padding: `${S.xs}px 0 ${S.md}px` }}>{t("settings.noStaff")}</div>}
          {staff.map((s) => (
            <div key={s.id} data-testid={`staff-row-${s.id}`} style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.card, borderRadius: R.md, padding: S.md, marginBottom: S.sm, border: `1px solid ${C.border}` }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: C.card2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="person" size={16} color={C.text} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>{s.display_name}</div>
                <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>@{s.username} · {s.role}</div>
              </div>
              <button data-testid={`edit-staff-${s.id}`} className="pressable" onClick={() => openEdit(s)} aria-label="Edit staff" style={{ background: "transparent", border: "none", padding: 8, flexShrink: 0 }}><Icon name="create" size={20} color={C.amber} /></button>
              <button data-testid={`remove-staff-${s.id}`} className="pressable" onClick={() => setDelStaff(s)} aria-label="Delete staff" style={{ background: "transparent", border: "none", padding: 8, flexShrink: 0 }}><Icon name="trash" size={20} color={C.red} /></button>
            </div>
          ))}
        </>
      )}

      <div style={{ height: S.xl }} />
      <div style={section}>{t("settings.notifications")}</div>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{t("settings.enableReminders")}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{t("settings.remindersSub")}</div>
          </div>
          <button data-testid="reminders-toggle" onClick={toggleReminders} style={{ width: 52, height: 30, borderRadius: 15, background: reminders ? C.green : C.card2, position: "relative", transition: "background 0.2s", border: "none" }}>
            <span style={{ position: "absolute", top: 3, left: reminders ? 25 : 3, width: 24, height: 24, borderRadius: 12, background: C.text, transition: "left 0.2s" }} />
          </button>
        </div>
        {!!remindMsg && <div data-testid="reminders-msg" style={{ color: C.amber, marginTop: S.md, fontWeight: 700, fontSize: 13 }}>{remindMsg}</div>}
      </div>
      {isManager && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{t("settings.showWindCheck")}</div>
              <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{t("settings.showWindCheckSub")}</div>
            </div>
            <button data-testid="windcheck-toggle" onClick={toggleWindCheck} style={{ width: 52, height: 30, borderRadius: 15, background: windCheck ? C.green : C.card2, position: "relative", transition: "background 0.2s", border: "none" }}>
              <span style={{ position: "absolute", top: 3, left: windCheck ? 25 : 3, width: 24, height: 24, borderRadius: 12, background: C.text, transition: "left 0.2s" }} />
            </button>
          </div>
        </div>
      )}

      <div style={section}>{t("settings.language")}</div>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: S.md }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>{t("settings.appLanguage")}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{t("settings.appLanguageSub")}</div>
          </div>
          <LanguageSwitcher align="right" />
        </div>
      </div>

      <div style={section}>{t("settings.installApp")}</div>
      <InstallSection />

      <div style={section}>{t("settings.legal")}</div>
      <div data-testid="terms-legal-btn" className="pressable" onClick={() => setTermsOpen(true)} style={linkCard}>
        <Icon name="shield-checkmark" size={20} color={C.amber} /><span style={{ color: C.text, fontSize: 16, fontWeight: 700, flex: 1 }}>{t("settings.termsLegal")}</span><Icon name="chevron-forward" size={18} color={C.muted} />
      </div>

      <div style={section}>{t("settings.help")}</div>
      <div data-testid="feedback-support-btn" className="pressable" onClick={() => nav("/feedback")} style={linkCard}>
        <Icon name="chatbubble-ellipses" size={20} color={C.amber} /><span style={{ color: C.text, fontSize: 16, fontWeight: 700, flex: 1 }}>{t("settings.feedbackSupport")}</span><Icon name="chevron-forward" size={18} color={C.muted} />
      </div>
      <div style={{ height: S.md }} />
      <Button title={t("settings.logOut")} variant="ghost" testID="logout-btn" onPress={async () => { await logout(); nav("/login"); }} />

      <button data-testid="settings-footer-terms" className="pressable" onClick={() => setTermsOpen(true)} style={{ display: "block", margin: "16px auto 0", color: C.muted, fontWeight: 700, fontSize: 12, background: "transparent", border: "none", textDecoration: "underline" }}>
        {t("settings.termsLegalShort")}
      </button>
      <div style={{ height: S.xxl }} />

      <InstallInstructions open={installOpen} onClose={() => setInstallOpen(false)} />
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />

      {staffModal && (
        <Modal onClose={() => setStaffModal(false)}>
          <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{t("settings.addStaffMember")}</div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: S.lg }}>{t("settings.staffModalSub")}</div>
          <Field label={t("settings.fullName")} testID="staff-name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          <Field label={t("settings.username")} testID="staff-username" autoCapitalize="none" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          {unameStatus === "checking" && <div style={{ color: C.muted, fontSize: 12, marginTop: -6, marginBottom: 10 }}>{t("settings.checkingUsername")}</div>}
          {unameStatus === "taken" && <div data-testid="staff-username-taken" style={{ color: C.red, fontSize: 12, marginTop: -6, marginBottom: 10, fontWeight: 700 }}>{t("settings.usernameTaken")}</div>}
          {unameStatus === "ok" && <div data-testid="staff-username-ok" style={{ color: C.green, fontSize: 12, marginTop: -6, marginBottom: 10, fontWeight: 700 }}>{t("settings.usernameAvailable")}</div>}
          <Field label={t("settings.passwordPin")} testID="staff-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.xs, textTransform: "uppercase" }}>{t("settings.role")}</div>
          <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
            {["operator", "manager"].map((r) => (
              <button key={r} data-testid={`staff-role-${r}`} onClick={() => setForm({ ...form, role: r })} style={{ flex: 1, background: form.role === r ? C.amber : C.card2, borderRadius: R.md, padding: `${S.md}px 0`, color: form.role === r ? C.black : C.text, fontWeight: 800, border: `1px solid ${C.borderStrong}` }}>{r === "operator" ? t("settings.roleOperator") : t("settings.roleManager")}</button>
            ))}
          </div>
          {!!err && <div style={{ color: C.red, marginBottom: S.sm, fontWeight: 600 }}>{err}</div>}
          <Button title={t("settings.createStaff")} testID="staff-submit" onPress={addStaff} loading={busy} />
          <button className="pressable" onClick={() => setStaffModal(false)} style={{ display: "block", margin: "12px auto 0", color: C.muted, fontWeight: 700 }}>{t("actions.cancel")}</button>
        </Modal>
      )}
      {editStaff && (
        <Modal onClose={() => setEditStaff(null)}>
          <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{t("settings.editStaffMember")}</div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: S.lg }}>{t("settings.editStaffSub")}</div>
          <Field label={t("settings.fullName")} testID="edit-staff-name" value={editForm.display_name} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} />
          <Field label={t("settings.username")} testID="edit-staff-username" autoCapitalize="none" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} />
          {editUnameStatus === "checking" && <div style={{ color: C.muted, fontSize: 12, marginTop: -6, marginBottom: 10 }}>{t("settings.checkingUsername")}</div>}
          {editUnameStatus === "taken" && <div data-testid="edit-staff-username-taken" style={{ color: C.red, fontSize: 12, marginTop: -6, marginBottom: 10, fontWeight: 700 }}>{t("settings.usernameTaken")}</div>}
          {editUnameStatus === "ok" && <div data-testid="edit-staff-username-ok" style={{ color: C.green, fontSize: 12, marginTop: -6, marginBottom: 10, fontWeight: 700 }}>{t("settings.usernameAvailable")}</div>}
          <Field label={t("settings.newPasswordPin")} testID="edit-staff-password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder={t("settings.leaveBlankKeep")} />
          <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.xs, textTransform: "uppercase" }}>{t("settings.role")}</div>
          <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
            {["operator", "manager"].map((r) => (
              <button key={r} data-testid={`edit-staff-role-${r}`} onClick={() => setEditForm({ ...editForm, role: r })} style={{ flex: 1, background: editForm.role === r ? C.amber : C.card2, borderRadius: R.md, padding: `${S.md}px 0`, color: editForm.role === r ? C.black : C.text, fontWeight: 800, border: `1px solid ${C.borderStrong}` }}>{r === "operator" ? t("settings.roleOperator") : t("settings.roleManager")}</button>
            ))}
          </div>
          {!!editErr && <div style={{ color: C.red, marginBottom: S.sm, fontWeight: 600 }}>{editErr}</div>}
          <Button title={t("settings.saveChanges")} testID="edit-staff-submit" onPress={saveStaffEdit} loading={editBusy} />
          <button className="pressable" onClick={() => setEditStaff(null)} style={{ display: "block", margin: "12px auto 0", color: C.muted, fontWeight: 700 }}>{t("actions.cancel")}</button>
        </Modal>
      )}
      {delStaff && (
        <Modal onClose={() => setDelStaff(null)}>
          <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900, marginBottom: 4 }}>{t("settings.deleteStaffTitle")}</div>
          <div style={{ color: C.muted, fontSize: 14, marginBottom: S.lg, lineHeight: 1.5 }}>{t("settings.deleteStaffPre")} <b style={{ color: C.text }}>{delStaff.display_name}</b> (@{delStaff.username}). {t("settings.deleteStaffPost")}</div>
          <button data-testid="confirm-delete-staff" className="pressable" onClick={confirmRemoveStaff} disabled={delBusy} style={{ width: "100%", background: C.red, color: C.text, border: "none", borderRadius: R.md, minHeight: 52, fontWeight: 900, fontSize: 16, opacity: delBusy ? 0.6 : 1 }}>{delBusy ? t("settings.deleting") : t("settings.deleteConfirm")}</button>
          <button className="pressable" onClick={() => setDelStaff(null)} style={{ display: "block", margin: "12px auto 0", color: C.muted, fontWeight: 700 }}>{t("actions.cancel")}</button>
        </Modal>
      )}
      {upgradeOpen && (
        <Modal onClose={() => setUpgradeOpen(false)}>
          <div style={{ display: "flex", alignItems: "center", gap: S.sm, marginBottom: S.sm }}>
            <Icon name="lock-closed" size={22} color={C.amber} />
            <div className="display" style={{ color: C.text, fontSize: 22, fontWeight: 900 }}>{t("settings.upgradeTitle")}</div>
          </div>
          <div style={{ color: C.muted, fontSize: 14, marginBottom: S.lg, lineHeight: 1.5 }}>{t("settings.upgradeBodyPre")} <b style={{ color: C.text }}>{sub?.tier_label || "current"}</b> {t("settings.upgradeBodyMid")} <b style={{ color: C.text }}>{seatLimit}</b> {t("settings.upgradeBodyPost")}</div>
          <Button title={t("settings.viewPlans")} testID="upgrade-plan-btn" onPress={() => { setUpgradeOpen(false); nav("/paywall"); }} />
          <button className="pressable" onClick={() => setUpgradeOpen(false)} style={{ display: "block", margin: "12px auto 0", color: C.muted, fontWeight: 700 }}>{t("settings.notNow")}</button>
        </Modal>
      )}
    </div>
  );
}
